import { useEffect, useState } from "react";

import { isSettledAt, stepCompletion, type CycleStep } from "@domain/steps.js";
import type { WorkspaceView } from "@domain/workspace-view.js";

import { fetchWorkspace, rememberCurrentStep } from "./api.js";
import { pathForStep, stepFromPath } from "./cycle-route.js";
import { ClarifyStep } from "./ClarifyStep.js";
import { DescribeStep } from "./DescribeStep.js";
import { StepRail } from "./StepRail.js";

export interface WorkspaceCycleProps {
  readonly tenantId: string;
}

/** The seven-step shell backed by the caller's persisted tenant records. */
export const WorkspaceCycle = ({
  tenantId,
}: WorkspaceCycleProps): React.JSX.Element => {
  const [view, setView] = useState<WorkspaceView | undefined>();
  const [failure, setFailure] = useState<string | undefined>();
  const [current, setCurrent] = useState<CycleStep>(
    () => stepFromPath(window.location.pathname) ?? "describe",
  );

  useEffect(() => {
    let live = true;
    void fetchWorkspace(tenantId)
      .then(async (loaded) => {
        if (!live) return;
        const routed = stepFromPath(window.location.pathname);
        const initial = routed ?? loaded.cycle.currentStep;
        const cycle =
          routed && routed !== loaded.cycle.currentStep
            ? await rememberCurrentStep(tenantId, routed)
            : loaded.cycle;
        if (!live) return;
        setView({ ...loaded, cycle });
        setCurrent(initial);
        if (!routed) window.history.replaceState(null, "", pathForStep(initial));
      })
      .catch((error: unknown) => {
        if (live) {
          setFailure(error instanceof Error ? error.message : String(error));
        }
      });
    return () => {
      live = false;
    };
  }, [tenantId]);

  useEffect(() => {
    const followHistory = (): void => {
      const routed = stepFromPath(window.location.pathname);
      if (!routed) return;
      setCurrent(routed);
      void rememberCurrentStep(tenantId, routed)
        .then((cycle) => {
          setView((existing) => existing && { ...existing, cycle });
        })
        .catch((error: unknown) => {
          setFailure(error instanceof Error ? error.message : String(error));
        });
    };
    window.addEventListener("popstate", followHistory);
    return () => window.removeEventListener("popstate", followHistory);
  }, [tenantId]);

  if (failure) {
    return <p role="alert">Could not load the workspace: {failure}</p>;
  }
  if (!view) {
    return <p aria-busy="true">Loading workspace…</p>;
  }

  const governed = view.governed[current] ?? [];
  const completion = stepCompletion(current, governed);

  const select = (step: CycleStep): void => {
    if (step === current) return;
    setFailure(undefined);
    void rememberCurrentStep(tenantId, step)
      .then((cycle) => {
        setView((existing) => existing && { ...existing, cycle });
        window.history.pushState(null, "", pathForStep(step));
        setCurrent(step);
      })
      .catch((error: unknown) => {
        setFailure(error instanceof Error ? error.message : String(error));
      });
  };

  const replaceCycle = (cycle: WorkspaceView["cycle"]): void => {
    setView((existing) => existing && { ...existing, cycle });
  };

  const replaceView = (updated: WorkspaceView): void => {
    setView(updated);
  };

  const showContinuedCycle = (cycle: WorkspaceView["cycle"]): void => {
    replaceCycle(cycle);
    window.history.pushState(null, "", pathForStep(cycle.currentStep));
    setCurrent(cycle.currentStep);
  };

  return (
    <>
      <p className="notice">
        Workspace <strong>{view.workspace.id}</strong> · cycle {view.cycle.id} ·
        live saved data
      </p>

      <StepRail governed={view.governed} current={current} onSelect={select} />

      {current === "describe" ? (
        <DescribeStep
          tenantId={tenantId}
          cycle={view.cycle}
          onCycleChanged={replaceCycle}
          onContinued={showContinuedCycle}
        />
      ) : current === "clarify" ? (
        <ClarifyStep
          tenantId={tenantId}
          view={view}
          onViewChanged={replaceView}
          onContinued={showContinuedCycle}
        />
      ) : (
        <section aria-live="polite" className="panel">
          <h2>{current}</h2>

          {governed.length === 0 ? (
            <p>Nothing has been created for this step yet.</p>
          ) : (
            <ul className="items">
              {governed.map((item) => (
                <li key={item.id}>
                  <span className="item-id">{item.id}</span>
                  <span className="item-state">{item.state}</span>
                  <span className="item-settled">
                    {isSettledAt(current, item.state)
                      ? "settled here"
                      : "outstanding here"}
                  </span>
                </li>
              ))}
            </ul>
          )}

          <button
            type="button"
            disabled={!completion.complete}
            className="continue"
          >
            Continue
          </button>
          {!completion.complete && (
            <p className="blocked">
              Waiting on {completion.outstanding.length}: {" "}
              {completion.outstanding.join(", ")}
            </p>
          )}
        </section>
      )}
    </>
  );
};
